/*
Take one GTIN string, which could be in any of the formats:
- GTIN-8
- GTIN-12
- GTIN-13
- GTIN-14
And it could be with or without the check digit. 
And it can be with or without the leading zeroes.
Determine which one it is, and add the check digit if needed.

Then build all possible other combinations from it as per above. I.e. all shorter and longer versions,
with and without check digit. With all possible number of leading zeroes.
*/
export function generateAllGTINCombinations(gtin: string): string[] {
  const STANDARD_LENGTHS = [8, 12, 13, 14];
  const variants = new Set<string>();

  // clean input: remove any non-digit characters
  const sanitized = gtin.replace(/\D/g, '');
  if (!sanitized) return [];

  // compute gs1 check digit for a numeric string (excluding check digit)
  function computeCheckDigit(s: string): string {
    const sum = s
      .split('')
      .map(d => parseInt(d, 10))
      .reverse()
      .reduce((acc, digit, idx) => acc + digit * (idx % 2 === 0 ? 3 : 1), 0);
    return ((10 - (sum % 10)) % 10).toString();
  }

  // generate base variants by stripping leading zeros from sanitized
  for (let i = 0; i <= sanitized.length; i++) {
    if (sanitized.startsWith('0'.repeat(i))) {
      const base = sanitized.slice(i);
      if (!base) continue;
      variants.add(base);
      // pad bases to standard lengths
      for (const length of STANDARD_LENGTHS) {
        if (base.length < length) {
          variants.add(base.padStart(length, '0'));
        }
      }
    }
  }

  // capture snapshot of current variants for check-digit handling
  const initialVariants = Array.from(variants);

  // add or remove check digits when valid
  for (const code of initialVariants) {
    for (const length of STANDARD_LENGTHS) {
      if (code.length === length - 1) {
        // append computed check digit
        variants.add(code + computeCheckDigit(code));
      } else if (code.length === length) {
        // remove valid check digit
        const body = code.slice(0, -1);
        if (computeCheckDigit(body) === code.slice(-1)) {
          variants.add(body);
        }
      }
    }
  }

  // strip any leading zeros from all variants
  const expanded = new Set(variants);
  for (const code of Array.from(variants)) {
    const match = code.match(/^0+/);
    if (match) {
      const count = match[0].length;
      for (let i = 1; i <= count; i++) {
        const stripped = code.slice(i);
        if (stripped) expanded.add(stripped);
      }
    }
  }

  // return sorted unique variants: by length, then lexicographically (digits-only)
  return Array.from(expanded).sort((a, b) => {
    const diff = a.length - b.length;
    if (diff !== 0) return diff;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });
}
